import React from 'react'
import { useForm } from 'react-hook-form'
import {
    Box,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Stack,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Alert
} from '@mui/material'
import { Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material'
import { maybeAttachAuthorization } from '@mahendran/shared-frontend-config'
import { buildApiUrl } from '../lib/apiBase'
import { useToast } from '../hooks/useToast'
import { useToastMessages } from '../utils/toastMessages'

type VideoSeries = {
    id: number
    slug: string
    title: string
    description?: string
    teaserImageUrl?: string
    createdAt: string
}

type SeriesFormData = {
    slug: string
    title: string
    description?: string
    teaserImageUrl?: string
}

const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
    const headers = new Headers(options.headers);
    const credentials = options.credentials || 'include';
    maybeAttachAuthorization(headers, credentials as RequestCredentials);
    const res = await fetch(url, { ...options, headers, credentials });
    return res;
}

const VideoSeriesManagement: React.FC = () => {
    const { toastPromise, showError } = useToast()
    const toastMessages = useToastMessages()
    const [series, setSeries] = React.useState<VideoSeries[]>([])
    const [loading, setLoading] = React.useState(true)
    const [loadError, setLoadError] = React.useState(false)
    const [pending, setPending] = React.useState(false)
    const mutationPending = React.useRef(false)
    const [dialog, setDialog] = React.useState<{ open: boolean, mode: 'create' | 'edit' | 'delete', series?: VideoSeries }>({ open: false, mode: 'create' })

    const { register, handleSubmit, reset, formState: { errors } } = useForm<SeriesFormData>()

    const loadSeries = async () => {
        setLoading(true)
        setLoadError(false)
        try {
            const res = await fetchWithAuth(buildApiUrl('/video-series'))
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            const data = await res.json()
            setSeries(Array.isArray(data) ? data : [])
        } catch (e: any) {
            setLoadError(true)
            showError(e?.message || toastMessages.seriesLoadError())
        } finally {
            setLoading(false)
        }
    }

    React.useEffect(() => { loadSeries() }, [])

    const openCreateDialog = () => {
        reset({ slug: '', title: '', description: '', teaserImageUrl: '' })
        setDialog({ open: true, mode: 'create' })
    }

    const openEditDialog = (series: VideoSeries) => {
        reset({
            slug: series.slug,
            title: series.title,
            description: series.description || '',
            teaserImageUrl: series.teaserImageUrl || ''
        })
        setDialog({ open: true, mode: 'edit', series })
    }

    const openDeleteDialog = (series: VideoSeries) => {
        setDialog({ open: true, mode: 'delete', series })
    }

    const onSubmit = async (data: SeriesFormData) => {
        if (mutationPending.current) return
        mutationPending.current = true
        setPending(true)
        const isEdit = dialog.mode === 'edit'
        const url = isEdit
            ? buildApiUrl(`/video-series/${dialog.series?.id}`)
            : buildApiUrl('/video-series')
        const method = isEdit ? 'PUT' : 'POST'

        const promise = fetchWithAuth(url, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        }).then(async (res) => {
            if (!res.ok) {
                let msg = ''
                try { const j = await res.clone().json(); msg = j?.detail || j?.title || '' } catch { /* ignore */ }
                if (!msg) { try { msg = await res.text() } catch { /* ignore */ } }
                throw new Error(msg || `HTTP ${res.status}`)
            }
            return res
        })

        return toastPromise(promise, {
            loading: toastMessages.saving(),
            success: () => {
                setDialog({ open: false, mode: 'create' })
                loadSeries()
                return isEdit ? toastMessages.seriesUpdated() : toastMessages.seriesCreated()
            },
            error: (e: any) => {
                return e?.message || toastMessages.seriesError()
            },
        }).catch(() => {
            // The toast displays the error; leave the dialog open for retry.
        }).finally(() => {
            mutationPending.current = false
            setPending(false)
        })
    }

    const onDelete = async () => {
        if (!dialog.series || mutationPending.current) return
        mutationPending.current = true
        setPending(true)

        const promise = fetchWithAuth(buildApiUrl(`/video-series/${dialog.series.id}`), { method: 'DELETE' }).then(async (res) => {
            if (!res.ok) throw new Error(await res.text() || `Fehler (${res.status})`)
            return res
        })

        return toastPromise(promise, {
            loading: 'Lösche...',
            success: () => {
                setDialog({ open: false, mode: 'create' })
                loadSeries()
                return toastMessages.seriesDeleted()
            },
            error: (e: any) => {
                return e?.message || toastMessages.seriesError()
            },
        }).catch(() => {
            // The toast displays the error; leave the dialog open for retry.
        }).finally(() => {
            mutationPending.current = false
            setPending(false)
        })
    }

    const closeDialog = () => {
        if (mutationPending.current) return
        setDialog({ open: false, mode: 'create' })
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h4">Video Serien Management</Typography>
                <Stack direction="row" spacing={1}>
                    <Button startIcon={<AddIcon />} variant="contained" onClick={openCreateDialog} disabled={loading || pending}>
                        Neue Serie
                    </Button>
                    <Button startIcon={<RefreshIcon />} onClick={loadSeries} disabled={loading || pending}>
                        Refresh
                    </Button>
                </Stack>
            </Stack>

            {loading && <Typography role="status">Serien werden geladen…</Typography>}
            {loadError && <Alert severity="error">Serien konnten nicht geladen werden. Bitte erneut versuchen.</Alert>}
            <Paper elevation={3}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Slug</TableCell>
                            <TableCell>Titel</TableCell>
                            <TableCell>Beschreibung</TableCell>
                            <TableCell>Erstellt</TableCell>
                            <TableCell align="right">Aktionen</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {series.map(s => (
                            <TableRow key={s.id} hover>
                                <TableCell>
                                    <Typography variant="body2" fontFamily="monospace">
                                        {s.slug}
                                    </Typography>
                                </TableCell>
                                <TableCell>{s.title}</TableCell>
                                <TableCell>
                                    {s.description ? (
                                        <Typography variant="body2" color="text.secondary">
                                            {s.description.length > 100
                                                ? `${s.description.substring(0, 100)}...`
                                                : s.description}
                                        </Typography>
                                    ) : '-'}
                                </TableCell>
                                <TableCell>
                                    {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '-'}
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton disabled={loading || pending} title="Bearbeiten" onClick={() => openEditDialog(s)}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton disabled={loading || pending} title="Löschen" onClick={() => openDeleteDialog(s)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!loading && !loadError && series.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">Keine Serien vorhanden</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Paper>

            {/* Create/Edit Dialog */}
            <Dialog open={dialog.open && (dialog.mode === 'create' || dialog.mode === 'edit')} onClose={closeDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {dialog.mode === 'edit' ? 'Serie bearbeiten' : 'Neue Serie erstellen'}
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ pt: 1 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    disabled={pending}
                                    label="Slug"
                                    placeholder="canada-eta"
                                    {...register('slug', {
                                        required: 'Slug ist erforderlich',
                                        pattern: {
                                            value: /^[a-z0-9-]+$/,
                                            message: 'Nur Kleinbuchstaben, Zahlen und Bindestriche erlaubt'
                                        }
                                    })}
                                    error={!!errors.slug}
                                    helperText={errors.slug?.message || 'Eindeutiger Bezeichner für die Serie'}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    disabled={pending}
                                    label="Titel"
                                    placeholder="Canada eTA Serie"
                                    {...register('title', { required: 'Titel ist erforderlich' })}
                                    error={!!errors.title}
                                    helperText={errors.title?.message}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    disabled={pending}
                                    label="Beschreibung"
                                    multiline
                                    minRows={3}
                                    {...register('description')}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    disabled={pending}
                                    label="Teaser Image URL"
                                    placeholder="https://example.com/teaser.jpg"
                                    {...register('teaserImageUrl')}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button disabled={pending} onClick={closeDialog}>Abbrechen</Button>
                    <Button variant="contained" disabled={pending} onClick={handleSubmit(onSubmit)}>
                        {dialog.mode === 'edit' ? 'Aktualisieren' : 'Erstellen'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={dialog.open && dialog.mode === 'delete'} onClose={closeDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Serie löschen</DialogTitle>
                <DialogContent>
                    Möchtest du die Serie "{dialog.series?.title}" wirklich löschen?
                    <br />
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        Achtung: Diese Aktion kann nicht rückgängig gemacht werden!
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button disabled={pending} onClick={closeDialog}>Abbrechen</Button>
                    <Button variant="contained" color="error" disabled={pending} onClick={onDelete}>
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default VideoSeriesManagement